import React, { useState, useEffect } from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, TextField, Typography, Box, CircularProgress,
  InputAdornment, TablePagination, Chip
} from '@mui/material';
import { Search } from 'lucide-react';
import AdminTopNavbar from './AdminTopNavbar';
import { useTheme } from '@mui/material/styles';
import "./outofstock.css"

const InStockProducts = () => {
  const theme = useTheme();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await fetch('/api/products');
      const data = await response.json();
      setProducts(data);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter only products that have at least one aroma size with quantity > 0
  const getInStockProducts = () => {
    return products.filter(product =>
      product.aromas?.some(aroma =>
        aroma.sizes?.some(size => size.quantity > 0)
      )
    );
  };

  const filteredProducts = getInStockProducts().filter(product => {
    const searchFields = [
      product.productName,
      product.productCode,
      product.categoryName,
      ...(product.aromas?.flatMap(aroma => [
        aroma.name,
        ...aroma.sizes.flatMap(size => [
          size.size.toString(),
          size.unit,
          size.sellingPrice.toString(),
          size.offer.toString()
        ])
      ]) || [])
    ];
    
    return searchFields.some(field =>
      field?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  const renderAromaInfo = (aromas) => {
    return aromas?.map((aroma, index) => {
      const inStockSizes = aroma.sizes.filter(size => size.quantity > 0);
      if (inStockSizes.length === 0) return null;

      return (
        <Box key={index} sx={{ mb: 1 }}>
          <Typography variant="subtitle2" color="primary">
            {aroma.name}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {inStockSizes.map((size, sizeIndex) => (
              <Chip
                key={sizeIndex}
                size="small"
                label={`${size.size}${size.unit} - ₹${size.sellingPrice} (${size.quantity} left)`}
                sx={{
                  backgroundColor: theme.palette.primary.light,
                  color: theme.palette.primary.contrastText,
                }}
              />
            ))}
          </Box>
        </Box>
      );
    });
  };

  if (loading) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" height="64vh">
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading products...</Typography>
      </Box>
    );
  }

  return (
    <>
      <AdminTopNavbar />
      <div className="containerorder" style={{ paddingTop: '200px' }}>
        <div style={{marginBottom:'-30px'}} className="row">
          <div className="col-xl-9 col-lg-12 order-xl-2 order-lg-2 order-md-2 order-2"
            style={{
              flexGrow: 1,
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              borderRadius: '8px',
              backgroundColor: '#fff',
              width: '100%',
              boxSizing: 'border-box',
            }}
          >
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 3,
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 2
            }}>
              <Typography variant="h4" component="h1" color="primary" sx={{ fontWeight: 'bold',marginTop:'10px' }}>
                In-Stock Products
              </Typography>
              <TextField
                placeholder="Search in product..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search color={theme.palette.primary.main} />
                    </InputAdornment>
                  ),
                }}
                sx={{  marginTop:'10px',width: { xs: '100%', sm: '300px' } }}
                size="small"
              />
            </Box>

            <TableContainer component={Paper} sx={{ mb: 3, boxShadow: 3, borderRadius: 2 }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: theme.palette.primary.main }}>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold',width:'100px' }}>Product Name</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold',width:'100px' }}>Category</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold',width:'100px' }}>Product Code</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold',width:"200px" }}>Aromas & Sizes (Qty)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredProducts
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((product) => (
                      <TableRow key={product._id}>
                        <TableCell>
                          <Typography variant="subtitle2" color="primary.dark">
                            {product.productName}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip label={product.categoryName} size="small" />
                        </TableCell>
                        <TableCell>{product.productCode}</TableCell>
                        <TableCell  style={{display:'flex',gap:'3px'}}>{renderAromaInfo(product.aromas)}</TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              className="custom-pagination"
              component="div"
              count={filteredProducts.length}
              page={page}
              rowsPerPage={rowsPerPage}
              onPageChange={(event, newPage) => setPage(newPage)}
              onRowsPerPageChange={(event) => {
                setRowsPerPage(parseInt(event.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[5, 10, 25]}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default InStockProducts;